/**
 * Canvas and texture plumbing.
 *
 * Every surface in the app is generated at runtime, so it ships with zero
 * image assets and works offline. The results are memoised: the same map is
 * shared by every record rather than rebuilt per swap.
 */

import * as THREE from 'three';

const cache = new Map<string, unknown>();

/** Build `key` once and hand back the same value forever after. */
export function memo<T>(key: string, make: () => T): T {
  const hit = cache.get(key);
  if (hit !== undefined) return hit as T;
  const made = make();
  cache.set(key, made);
  return made;
}

export function canvas(size: number, h: number = size): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = size;
  c.height = h;
  return c;
}

/** 2D context, asserted non-null: a canvas element always grants one. */
export function ctx2d(
  c: HTMLCanvasElement,
  options?: CanvasRenderingContext2DSettings,
): CanvasRenderingContext2D {
  const g = c.getContext('2d', options);
  if (!g) throw new Error('2D canvas context unavailable');
  return g;
}

export interface TexOptions {
  srgb?: boolean;
  aniso?: number;
  repeat?: readonly [number, number] | null;
  renderer?: THREE.WebGLRenderer | null;
}

export function tex(
  c: HTMLCanvasElement,
  { srgb = false, aniso = 8, repeat = null, renderer = null }: TexOptions = {},
): THREE.CanvasTexture {
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
  t.anisotropy = renderer ? Math.min(aniso, renderer.capabilities.getMaxAnisotropy()) : aniso;
  t.wrapS = repeat ? THREE.RepeatWrapping : THREE.ClampToEdgeWrapping;
  t.wrapT = t.wrapS;
  if (repeat) t.repeat.set(repeat[0], repeat[1]);
  t.needsUpdate = true;
  return t;
}

/** An sRGB texture built fresh each call — for per-record maps that get disposed. */
export function ownedTex(c: HTMLCanvasElement, aniso = 8): THREE.CanvasTexture {
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = aniso;
  t.needsUpdate = true;
  return t;
}

interface Disposable {
  dispose?: () => void;
}

export function disposeTextureCache(): void {
  for (const v of cache.values()) {
    const d = v as Disposable;
    if (typeof d.dispose === 'function') d.dispose();
    else if (v && typeof v === 'object') {
      for (const x of Object.values(v)) (x as Disposable | undefined)?.dispose?.();
    }
  }
  cache.clear();
}
